import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { v: "individual", l: "Individual" }, { v: "small_group", l: "Small group" },
  { v: "thematic", l: "Thematic" }, { v: "community", l: "Community" },
];

export default function AdminMeetings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", starts_at: "", kind: "small_group", zoom_link: "" });

  const load = () => api.get("/meetings").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.starts_at) return toast.error("Title and date are required.");
    try {
      await api.post("/meetings", { ...form, starts_at: new Date(form.starts_at).toISOString() });
      setForm({ title: "", description: "", starts_at: "", kind: "small_group", zoom_link: "" });
      setOpen(false);
      toast.success("Meeting created.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/meetings/${id}`); load(); toast.success("Removed."); } catch {}
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-heading text-3xl tracking-tight">Meetings</h1>
        <Button className="btn-gold rounded-full text-sm font-medium" onClick={() => setOpen(!open)} data-testid="new-meeting-btn">
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>
      <p className="text-muted-foreground mb-8 text-sm">Organise Zoom meetings: individual, small groups, thematic or community.</p>

      {open && (
        <div className="bg-card border border-border rounded-2xl p-6 mb-8 space-y-4 animate-fade-in-up">
          <div>
            <Label>Title</Label>
            <Input className="mt-2 rounded-xl" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meeting-title-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Date and time</Label>
              <Input type="datetime-local" className="mt-2 rounded-xl" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="meeting-date-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-2 rounded-xl" data-testid="meeting-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Zoom link (optional)</Label>
            <Input className="mt-2 rounded-xl" placeholder="https://zoom.us/…" value={form.zoom_link} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} data-testid="meeting-zoom-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea className="mt-2 rounded-xl" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="meeting-desc-input" />
          </div>
          <Button className="btn-gold rounded-full text-sm font-medium" onClick={create} data-testid="meeting-submit-btn">Create meeting</Button>
        </div>
      )}

      <div className="space-y-3" data-testid="admin-meetings-list">
        {items.map((m) => (
          <div key={m.id} className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between">
            <div>
              <p className="font-heading text-lg">{m.title}</p>
              <p className="text-sm text-muted-foreground">{new Date(m.starts_at).toLocaleString("en-GB")}{m.attendee_count != null ? ` · ${m.attendee_count} registered` : ""}</p>
            </div>
            <button onClick={() => remove(m.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-meeting-${m.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
